import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getCurrencySymbol } from '../lib/currency';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeftRight, Download, Loader2, ChevronDown, Check, TrendingUp, TrendingDown,
  BarChart3, Users, Clock, DollarSign, Printer, FileText, FileSpreadsheet, Mail,
} from 'lucide-react';
import EmailShareDialog from '../components/EmailShareDialog';
import CompareScenarioPanel from '../components/CompareScenarioPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { key: 'metrics',   label: 'Key Metrics' },
  { key: 'groups',    label: 'Cost by Group' },
  { key: 'roles',     label: 'Effort by Role' },
  { key: 'timeline',  label: 'Timeline' },
  { key: 'resources', label: 'Resource Allocation' },
  { key: 'clientRes', label: 'Customer Resources' },
  { key: 'risk',      label: 'Risk Factors' },
  { key: 'narrative', label: 'Exec Summary' },
];

function DeltaCell({ diff, suffix = '', invert = false }) {
  if (diff === 0) return <span className="text-slate-400">&mdash;</span>;
  const isNeg = diff < 0;
  const good = invert ? !isNeg : isNeg;
  return (
    <span className={`font-semibold text-xs ${good ? 'text-emerald-600' : 'text-red-500'}`}>
      {good ? <TrendingDown className="w-3 h-3 inline mr-0.5" /> : <TrendingUp className="w-3 h-3 inline mr-0.5" />}
      {diff > 0 ? '+' : ''}{diff.toLocaleString(undefined, { maximumFractionDigits: 1 })}{suffix}
    </span>
  );
}

function WinnerBadge({ winner, nameA, nameB }) {
  if (winner === 'tie') return <Badge variant="outline" className="text-[9px] text-slate-400">Tie</Badge>;
  const name = winner === 'a' ? nameA : nameB;
  return <Badge className="text-[9px] bg-emerald-100 text-emerald-700 border-emerald-200">{name}</Badge>;
}

export default function CompareQuotesPage() {
  const [searchParams] = useSearchParams();
  const [estimates, setEstimates] = useState([]);
  const [idA, setIdA] = useState(searchParams.get('a') || '');
  const [idB, setIdB] = useState(searchParams.get('b') || '');
  const _cqCodes = new Set([idA, idB].map(id => ((estimates.find(e => e.id === id) || {}).currency_code || 'USD').toUpperCase()));
  const cqSym = _cqCodes.size === 1 ? getCurrencySymbol([..._cqCodes][0]) : '$';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(() => Object.fromEntries(SECTIONS.map(s => [s.key, true])));
  const [exporting, setExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  // Scenario state (shared with child)
  const [scenarioActive, setScenarioActive] = useState(false);
  const [originalData, setOriginalData] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/estimates`, { withCredentials: true })
      .then(res => setEstimates(res.data?.estimates || []))
      .catch(() => {});
  }, []);

  const runCompare = useCallback(async () => {
    if (!idA || !idB || idA === idB) return;
    setLoading(true);
    setData(null);
    setOriginalData(null);
    setScenarioActive(false);
    try {
      const res = await axios.get(`${API_URL}/api/export/compare-quotes-data`, {
        params: { id_a: idA, id_b: idB }, withCredentials: true,
      });
      setData(res.data);
      setOriginalData(res.data);
    } catch {
      // error
    } finally {
      setLoading(false);
    }
  }, [idA, idB]);

  useEffect(() => {
    if (idA && idB && idA !== idB) runCompare();
  }, [idA, idB, runCompare]);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const res = await axios.post(`${API_URL}/api/export/compare-quotes`,
        { id_a: idA, id_b: idB, format }, { responseType: 'blob', withCredentials: true });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `BC_Compare.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // error
    } finally {
      setExporting(false);
      setExportOpen(false);
    }
  };

  const generatePdfBase64 = async () => {
    const res = await axios.post(`${API_URL}/api/export/compare-quotes`,
      { id_a: idA, id_b: idB, format: 'pdf' }, { responseType: 'arraybuffer', withCredentials: true });
    return btoa(new Uint8Array(res.data).reduce((d, b) => d + String.fromCharCode(b), ''));
  };

  const toggleAll = (on) => setVisible(Object.fromEntries(SECTIONS.map(s => [s.key, on])));

  const a = data?.a;
  const b = data?.b;
  const deltas = data?.deltas;
  const nameA = a?.estimate?.estimate_name || 'Estimate A';
  const nameB = b?.estimate?.estimate_name || 'Estimate B';

  return (
    <>
    <style>{`
      @media print {
        /* Hide non-content elements */
        nav, header, [data-testid="scenario-slider-panel"],
        [data-testid="compare-print-btn"],
        [data-testid="compare-export-btn"],
        button:not([data-testid]) { }

        /* Each Card section avoids page breaks */
        [data-testid="compare-metrics"],
        [data-testid="compare-groups"],
        [data-testid="compare-roles"],
        [data-testid="compare-timeline"],
        [data-testid="compare-resources"],
        [data-testid="compare-client-resources"],
        [data-testid="compare-risk"],
        [data-testid="compare-narrative"] {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          page-break-after: auto;
          break-after: auto;
        }

        /* Tables: rows don't break, header repeats */
        table { page-break-inside: auto; break-inside: auto; }
        tr { page-break-inside: avoid !important; break-inside: avoid !important; }
        thead { display: table-header-group; }

        /* Section headings stay with content */
        h1, h2, h3 { page-break-after: avoid !important; break-after: avoid !important; }

        /* Prevent orphan/widow lines */
        p { orphans: 3; widows: 3; }

        /* Preserve colors */
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }

        /* Grid sub-sections stay together */
        .grid { page-break-inside: avoid !important; break-inside: avoid !important; }
      }
    `}</style>
    <main className="w-full px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0F2040] font-['Outfit'] flex items-center gap-2">
              <ArrowLeftRight className="w-6 h-6 text-[#4ABFCF]" /> Compare Quotes
            </h1>
            <p className="text-sm text-slate-500 mt-1">Side-by-side comparison with delta analysis</p>
          </div>
          {data && (
            <div className="flex items-center gap-2">
              <div className="relative">
                <Button variant="outline" size="sm" onClick={() => setExportOpen(!exportOpen)}
                  disabled={exporting} className="text-xs" data-testid="compare-export-btn">
                  {exporting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Download className="w-3 h-3 mr-1" />}
                  Export <ChevronDown className="w-3 h-3 ml-1" />
                </Button>
                {exportOpen && (
                  <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-50 py-1 min-w-[140px]">
                    {[['pdf', 'PDF', FileText], ['docx', 'Word', FileText], ['xlsx', 'Excel', FileSpreadsheet], ['pptx', 'PowerPoint', FileText]].map(([fmt, label, Icon]) => (
                      <button key={fmt} onClick={() => handleExport(fmt)}
                        className="w-full px-3 py-1.5 text-left text-xs hover:bg-slate-50 flex items-center gap-2" data-testid={`compare-export-${fmt}`}>
                        <Icon className="w-3 h-3" /> {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <EmailShareDialog onGeneratePdf={generatePdfBase64}
                defaultSubject={`Quote Comparison — ${nameA} vs ${nameB}`}
                defaultFilename="BC_Compare.pdf" triggerClassName="text-xs" />
              <Button variant="outline" size="sm" className="text-xs"
                onClick={() => window.print()} data-testid="compare-print-btn">
                <Printer className="w-3 h-3 mr-1" /> Print
              </Button>
            </div>
          )}
        </div>

        {/* Estimate selectors */}
        <Card className="mb-6">
          <CardContent className="pt-4 pb-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Estimate A</label>
                <select value={idA} onChange={e => setIdA(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#4ABFCF]/30 focus:border-[#4ABFCF]"
                  data-testid="compare-select-a">
                  <option value="">Select estimate...</option>
                  {estimates.map(e => <option key={e.id} value={e.id}>{e.estimate_name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Estimate B</label>
                <select value={idB} onChange={e => setIdB(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#4ABFCF]/30 focus:border-[#4ABFCF]"
                  data-testid="compare-select-b">
                  <option value="">Select estimate...</option>
                  {estimates.map(e => <option key={e.id} value={e.id}>{e.estimate_name}</option>)}
                </select>
              </div>
            </div>
            {idA && idB && idA === idB && (
              <p className="text-xs text-amber-600 mt-2">Please select two different estimates to compare.</p>
            )}
          </CardContent>
        </Card>

        {loading && (
          <div className="flex items-center justify-center py-20 gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-[#4ABFCF]" />
            <span className="text-slate-500">Building comparison...</span>
          </div>
        )}

        {data && (
          <>
            {/* Scenario Slider Panel */}
            <CompareScenarioPanel
              currencySymbol={cqSym}
              idA={idA} idB={idB} nameA={nameA} nameB={nameB}
              originalData={originalData}
              scenarioActive={scenarioActive} setScenarioActive={setScenarioActive}
              data={data} setData={setData}
            />

            {/* Section toggles */}
            <div className="flex flex-wrap items-center gap-2 mb-5">
              <span className="text-xs font-medium text-slate-400 mr-1">Sections:</span>
              {SECTIONS.map(s => (
                <button key={s.key} onClick={() => setVisible(v => ({ ...v, [s.key]: !v[s.key] }))}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium border transition-all
                    ${visible[s.key] ? 'bg-[#0F2040] text-white border-[#0F2040]' : 'bg-white text-slate-400 border-slate-200'}`}
                  data-testid={`compare-toggle-${s.key}`}>
                  {visible[s.key] && <Check className="w-2.5 h-2.5" />}{s.label}
                </button>
              ))}
              <button onClick={() => toggleAll(true)} className="text-[10px] text-[#4ABFCF] hover:underline ml-2">All</button>
              <button onClick={() => toggleAll(false)} className="text-[10px] text-slate-400 hover:underline">None</button>
            </div>

            {/* Key Metrics */}
            {visible.metrics && (
              <Card className="mb-4" data-testid="compare-metrics">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-[#0F2040] flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-[#4ABFCF]" /> Key Metrics
                    {scenarioActive && <Badge className="text-[8px] bg-amber-50 text-amber-600 border-amber-200">Scenario</Badge>}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <thead><tr className="bg-[#0F2040] text-white text-xs">
                      <th className="text-left px-3 py-2 w-[20%]">Metric</th>
                      <th className="text-left px-3 py-2 w-[25%]">{nameA}</th>
                      <th className="text-left px-3 py-2 w-[25%]">{nameB}</th>
                      <th className="text-left px-3 py-2 w-[15%]">Delta</th>
                      <th className="text-center px-3 py-2 w-[15%]">Winner</th>
                    </tr></thead>
                    <tbody>
                      {[
                        { label: 'Total Effort', k: 'total_effort', fmt: (v) => `${v.toLocaleString()}h` },
                        { label: 'Total Cost', k: 'total_cost', fmt: (v) => `${cqSym}${v.toLocaleString()}` },
                        { label: 'Components', k: 'component_count', fmt: (v) => v },
                        { label: 'Duration', k: 'duration_weeks', fmt: (v) => `${v} weeks` },
                        { label: 'Peak FTE', k: 'peak_fte', fmt: (v) => v?.toFixed(1) },
                      ].map(({ label, k, fmt }, i) => {
                        const d = deltas[k] || {};
                        return (
                          <tr key={k} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="px-3 py-2 font-medium text-[#0F2040]">{label}</td>
                            <td className="px-3 py-2">{fmt(d.a || 0)}</td>
                            <td className="px-3 py-2">{fmt(d.b || 0)}</td>
                            <td className="px-3 py-2"><DeltaCell diff={d.diff || 0} suffix={k === 'total_cost' ? '' : k.includes('week') ? 'wk' : 'h'} /></td>
                            <td className="px-3 py-2 text-center"><WinnerBadge winner={d.winner} nameA={nameA} nameB={nameB} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Group Comparison */}
            {visible.groups && (
              <Card className="mb-4" data-testid="compare-groups">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-[#0F2040] flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-[#4ABFCF]" /> Cost & Effort by Group
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <thead><tr className="bg-[#0F2040] text-white text-xs">
                      <th className="text-left px-3 py-2">Group</th>
                      <th className="text-left px-3 py-2">{nameA}</th>
                      <th className="text-left px-3 py-2">{nameB}</th>
                      <th className="text-left px-3 py-2">Effort Delta</th>
                      <th className="text-left px-3 py-2">Cost Delta</th>
                    </tr></thead>
                    <tbody>
                      {(deltas.groups || []).map((g, i) => (
                        <tr key={`group-${g.name}`} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          <td className="px-3 py-2 font-medium text-[#0F2040]">{g.name}</td>
                          <td className="px-3 py-2">{g.effort_a.toLocaleString()}h / ${g.cost_a.toLocaleString()}</td>
                          <td className="px-3 py-2">{g.effort_b.toLocaleString()}h / ${g.cost_b.toLocaleString()}</td>
                          <td className="px-3 py-2"><DeltaCell diff={g.effort_diff} suffix="h" /></td>
                          <td className="px-3 py-2"><DeltaCell diff={g.cost_diff} suffix="" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Role Comparison */}
            {visible.roles && (
              <Card className="mb-4" data-testid="compare-roles">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-[#0F2040] flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#4ABFCF]" /> Effort by Role
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <thead><tr className="bg-[#0F2040] text-white text-xs">
                      <th className="text-left px-3 py-2">Role</th>
                      <th className="text-left px-3 py-2">{nameA}</th>
                      <th className="text-left px-3 py-2">{nameB}</th>
                      <th className="text-left px-3 py-2">Delta</th>
                    </tr></thead>
                    <tbody>
                      {(deltas.roles || []).map((r, i) => (
                        <tr key={`role-${r.role || r.role_label}`} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          <td className="px-3 py-2 font-medium text-[#0F2040]">{r.role_label}</td>
                          <td className="px-3 py-2">{r.hours_a.toLocaleString()}h</td>
                          <td className="px-3 py-2">{r.hours_b.toLocaleString()}h</td>
                          <td className="px-3 py-2"><DeltaCell diff={r.diff} suffix="h" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Timeline Comparison */}
            {visible.timeline && a?.timeline && b?.timeline && (
              <Card className="mb-4" data-testid="compare-timeline">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-[#0F2040] flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#4ABFCF]" /> Timeline Comparison
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs font-semibold text-slate-500 mb-1">{nameA}</p>
                      <p className="text-lg font-bold text-[#0F2040]">{a.timeline.total_duration_weeks} weeks</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs font-semibold text-slate-500 mb-1">{nameB}</p>
                      <p className="text-lg font-bold text-[#0F2040]">{b.timeline.total_duration_weeks} weeks</p>
                    </div>
                  </div>
                  <table className="w-full text-sm">
                    <thead><tr className="bg-[#0F2040] text-white text-xs">
                      <th className="text-left px-3 py-2">Phase</th>
                      <th className="text-left px-3 py-2">{nameA} Duration</th>
                      <th className="text-left px-3 py-2">{nameB} Duration</th>
                      <th className="text-left px-3 py-2">Delta</th>
                    </tr></thead>
                    <tbody>
                      {(() => {
                        const pa = Object.fromEntries((a.timeline.phases || []).map(p => [p.name, p]));
                        const pb = Object.fromEntries((b.timeline.phases || []).map(p => [p.name, p]));
                        const names = [...new Set([...Object.keys(pa), ...Object.keys(pb)])];
                        return names.map((name, i) => {
                          const da = pa[name]?.duration_weeks || 0;
                          const db = pb[name]?.duration_weeks || 0;
                          return (
                            <tr key={`phase-${name}`} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                              <td className="px-3 py-2 font-medium text-[#0F2040]">{name}</td>
                              <td className="px-3 py-2">{da} wk</td>
                              <td className="px-3 py-2">{db} wk</td>
                              <td className="px-3 py-2"><DeltaCell diff={db - da} suffix=" wk" /></td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Resource Allocation Comparison */}
            {visible.resources && a?.timeline && b?.timeline && (
              <Card className="mb-4" data-testid="compare-resources">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-[#0F2040] flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#4ABFCF]" /> Resource Allocation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <thead><tr className="bg-[#0F2040] text-white text-xs">
                      <th className="text-left px-3 py-2">Role</th>
                      <th className="text-left px-3 py-2">{nameA} Hours</th>
                      <th className="text-left px-3 py-2">{nameB} Hours</th>
                      <th className="text-left px-3 py-2">{nameA} Peak FTE</th>
                      <th className="text-left px-3 py-2">{nameB} Peak FTE</th>
                    </tr></thead>
                    <tbody>
                      {(() => {
                        const ra = a.timeline.role_summary || {};
                        const rb = b.timeline.role_summary || {};
                        const roles = [...new Set([...Object.keys(ra), ...Object.keys(rb)])];
                        return roles.map((rk, i) => (
                          <tr key={`role-${rk}`} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="px-3 py-2 font-medium text-[#0F2040]">{ra[rk]?.role_label || rb[rk]?.role_label || rk}</td>
                            <td className="px-3 py-2">{(ra[rk]?.total_hours || 0).toLocaleString()}h</td>
                            <td className="px-3 py-2">{(rb[rk]?.total_hours || 0).toLocaleString()}h</td>
                            <td className="px-3 py-2">{(ra[rk]?.peak_fte || 0).toFixed(2)}</td>
                            <td className="px-3 py-2">{(rb[rk]?.peak_fte || 0).toFixed(2)}</td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Customer Resources */}
            {visible.clientRes && (a?.timeline?.client_resources?.length > 0 || b?.timeline?.client_resources?.length > 0) && (
              <Card className="mb-4" data-testid="compare-client-resources">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-[#0F2040]">Customer Resources</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    {[{ label: nameA, cr: a?.timeline?.client_resources || [] }, { label: nameB, cr: b?.timeline?.client_resources || [] }].map(({ label, cr }) => (
                      <div key={`client-res-${label}`}>
                        <p className="text-xs font-semibold text-slate-500 mb-2">{label}</p>
                        {cr.length === 0 ? <p className="text-xs text-slate-400">No data</p> : (
                          <table className="w-full text-xs">
                            <thead><tr className="bg-slate-100"><th className="text-left px-2 py-1">Role</th><th className="text-left px-2 py-1">Avg %</th><th className="text-left px-2 py-1">Peak %</th></tr></thead>
                            <tbody>{cr.map((c) => (
                              <tr key={c.role || c.label}><td className="px-2 py-1">{c.label}</td><td className="px-2 py-1">{c.avg_commitment}%</td><td className="px-2 py-1">{c.peak_pct}%</td></tr>
                            ))}</tbody>
                          </table>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Risk Factors */}
            {visible.risk && (
              <Card className="mb-4" data-testid="compare-risk">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-[#0F2040]">Risk & Complexity Factors</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    {[{ label: nameA, mods: a?.estimate?.modifiers || {} }, { label: nameB, mods: b?.estimate?.modifiers || {} }].map(({ label, mods }) => (
                      <div key={`risk-${label}`}>
                        <p className="text-xs font-semibold text-slate-500 mb-2">{label}</p>
                        {Object.keys(mods).length === 0 ? <p className="text-xs text-slate-400">No modifiers</p> : (
                          <div className="space-y-1">
                            {Object.entries(mods).sort().map(([k, v]) => (
                              <div key={k} className="flex items-center justify-between text-xs">
                                <span className="text-slate-600">{k.replace(/_/g, ' ')}</span>
                                <Badge variant="outline" className={`text-[9px] ${v > 1 ? 'text-red-500' : v < 1 ? 'text-emerald-600' : 'text-slate-400'}`}>{v.toFixed(2)}</Badge>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Executive Summaries */}
            {visible.narrative && (
              <Card className="mb-4" data-testid="compare-narrative">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-[#0F2040]">Executive Summaries</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    {[{ label: nameA, narr: a?.narrative }, { label: nameB, narr: b?.narrative }].map(({ label, narr }) => (
                      <div key={`narr-${label}`}>
                        <p className="text-xs font-semibold text-slate-500 mb-2">{label}</p>
                        {narr?.executive_summary ? (
                          <p className="text-xs text-slate-600 leading-relaxed">{narr.executive_summary.slice(0, 600)}{narr.executive_summary.length > 600 ? '...' : ''}</p>
                        ) : <p className="text-xs text-slate-400">No summary available</p>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {!data && !loading && idA && idB && idA !== idB && (
          <div className="relative overflow-hidden text-center py-16 text-slate-400" data-testid="compare-quotes-empty-pending">
            <img
              src="/seertech-mark.png"
              alt=""
              aria-hidden="true"
              className="pointer-events-none select-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 opacity-[0.05]"
            />
            <ArrowLeftRight className="w-10 h-10 mx-auto mb-3 opacity-30 relative" />
            <p className="relative">Select two estimates above to begin comparison</p>
          </div>
        )}
        {!idA && !idB && (
          <div className="relative overflow-hidden text-center py-16 text-slate-400" data-testid="compare-quotes-empty-initial">
            <img
              src="/seertech-mark.png"
              alt=""
              aria-hidden="true"
              className="pointer-events-none select-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 opacity-[0.05]"
            />
            <ArrowLeftRight className="w-10 h-10 mx-auto mb-3 opacity-30 relative" />
            <p className="text-sm relative">Select two estimates above to compare their quotes side-by-side</p>
          </div>
        )}
      </main>
    </>
  );
}
